import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  isValidView,
  hasViewPermission,
  getViewsForType,
} from '@/utils/viewHelpers';
import { ViewPaths, ViewConfig } from '@/types/views';
import {
  useGetOrganizationByID,
  useGetTenant,
  useListPayersForTenant,
} from '@/api/trafikportalen';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useMemo } from 'react';
import { Loading } from '@/components/Loading';
import { DataNotFound } from '@/components/DataNotFound';
import Header from '@/components/Header/Header';
import { HeaderMenu } from '@/components/Header/HeaderMenu';
import { ActionButtons } from '@/components/Header/ActionButtons';
import { IconEyeArrow } from '@/icons';
import Button from '@/components/core/Button';
import { OrganizationDetailsForm } from '@/components/Organizations/OrganizationDetailsForm';
import { OrganizationTicketSettings } from '@/components/Organizations/OrganizationTicketSettings';
import { CenteredColumn } from '@/components/CenteredColumn';
import { useImpersonation } from '@/hooks/useImpersonation';
import { OrganizationPayers } from '@/components/Organizations/OrganizationPayers';
import { DocumentHead } from '@/components/core/DocumentHead';
import { OrganizationAdvanced } from '@/components/Organizations/OrganizationAdvanced';
import { OrganizationStudentSettings } from '@/components/Organizations/OrganizationStudentSettings';
import { useStudentsDisabled } from '@/contexts/ConfigContext';

export const Route = createFileRoute(
  '/$tenantId/_layout/organizations/organization/$organizationId/$view',
)({
  component: OrganizationView,
  beforeLoad: ({ params: { tenantId, view } }) => {
    if (
      !isValidView(view, 'organization') ||
      !hasViewPermission(view, 'organization', 'tenant:viewer')
    ) {
      throw redirect({
        to: '/$tenantId',
        params: { tenantId },
        replace: true,
      });
    }
  },
});

function OrganizationView() {
  const { tenantId, organizationId, view } = Route.useParams();
  const { data: tenant } = useGetTenant();
  const { data: payers } = useListPayersForTenant();
  const selected = view as ViewPaths<'organization'>;
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { data: organization, isLoading } =
    useGetOrganizationByID(organizationId);
  const impersonate = useImpersonation();
  const studentsDisabled = useStudentsDisabled();

  const notablePayers = useMemo(() => {
    if (!tenant || !organization || !payers) return [];

    const count = payers.reduce((count, payer) => {
      if (payer.organization_id !== organizationId) return count;

      const requiredProps = organization.is_internal
        ? tenant?.required_internal_invoicing_details
        : tenant?.required_external_invoicing_details;

      const missingProps = requiredProps?.filter(
        (prop) => !payer.details[prop],
      );

      return count + (missingProps?.length > 0 ? 1 : 0);
    }, 0);

    return count > 0 ? ['payers'] : [];
  }, [tenant, organization, payers, organizationId]);

  const onViewChange = useCallback(
    (view?: string) => {
      if (!view) return;

      navigate({
        to: Route.to,
        params: { tenantId, organizationId, view },
        replace: false,
      });
    },
    [navigate, tenantId, organizationId],
  );

  const views = useMemo(() => {
    const all = getViewsForType('organization') as ViewConfig[];
    if (studentsDisabled) {
      return all.filter((v) => v.path !== 'students');
    }
    return all;
  }, [studentsDisabled]);

  const metadataKeys = useMemo(() => {
    const organizationName = organization?.name || 'Organisation';

    switch (selected) {
      case 'information':
        return {
          title: `${organizationName} - Information`,
          description: `Information och inställningar för organisationen ${organizationName}`,
        };
      case 'tickets':
        return {
          title: `${organizationName} - Biljetter`,
          description: `Biljettinställningar för organisationen ${organizationName}`,
        };
      case 'students':
        return {
          title: `${organizationName} - Elevhantering`,
          description: `Elevhanteringsinställningar för organisationen ${organizationName}`,
        };
      case 'payers':
        return {
          title: `${organizationName} - Fakturamottagare`,
          description: `Fakturamottagare för organisationen ${organizationName}`,
        };
      case 'advanced':
        return {
          title: `${organizationName} - Avancerat`,
          description: `Avancerade inställningar för organisationen ${organizationName}`,
        };
      default:
        return {
          title: `${organizationName}`,
          description: `Detaljer för organisationen ${organizationName}`,
        };
    }
  }, [selected, organization?.name]);

  if (isLoading) {
    return <Loading />;
  }

  if (!organization) {
    return (
      <DataNotFound
        description={formatMessage({
          defaultMessage: 'Organisationen finns inte',
        })}
        buttonTitle={formatMessage({
          defaultMessage: 'Till organisationer',
        })}
        onClick={() =>
          navigate({ to: '/$tenantId/organizations', params: { tenantId } })
        }
      />
    );
  }

  return (
    <div className="overflow-y-auto">
      <DocumentHead
        title={metadataKeys.title}
        description={metadataKeys.description}
      />
      <Header
        title={organization.name}
        menu={
          <HeaderMenu
            options={views}
            selected={selected}
            onViewChange={onViewChange}
            notificationViews={notablePayers}
          />
        }
        actions={
          organization.state === 'approved' && (
            <ActionButtons>
              <Button
                startIcon={<IconEyeArrow />}
                onClick={() => impersonate(organizationId)}
                variant="outline"
              >
                <FormattedMessage defaultMessage="Visa som organisation" />
              </Button>
            </ActionButtons>
          )
        }
      />
      <CenteredColumn>
        {selected === 'information' && (
          <OrganizationDetailsForm organization={organization} />
        )}
        {selected === 'tickets' && (
          <OrganizationTicketSettings organization={organization} />
        )}
        {selected === 'students' && !studentsDisabled && (
          <OrganizationStudentSettings organization={organization} />
        )}
        {selected === 'payers' && (
          <OrganizationPayers organization={organization} />
        )}
        {selected === 'advanced' && (
          <OrganizationAdvanced organization={organization} />
        )}
      </CenteredColumn>
    </div>
  );
}
